import { useEffect, useState } from 'react'

import { useFeatureFlag } from '@/app/lib/feature-flagging/context.js'
import { resolveHref, resolveHrefFragment } from '@/app/lib/resolveHref.js'

import { FragmentType } from '../../../lib/cms/index.js'
import { Gutter } from '../../Gutter/index.jsx'
import { HeaderDisplay } from '../index.js'
import { HorizontalMenu } from './HorizontalMenu.jsx'
import { headerNavbarFragment } from './menu-fragment.js'
import { VerticalMenu } from './VerticalMenu.js'

interface HeaderProps {
  menuItems: FragmentType<typeof headerNavbarFragment>[]
  headerDisplay: HeaderDisplay
  signupLink: FragmentType<typeof resolveHrefFragment> | undefined | null
  loginLink: FragmentType<typeof resolveHrefFragment> | undefined | null
  supportLink: FragmentType<typeof resolveHrefFragment> | undefined | null
}

export interface NavbarCtaLinks {
  signup: string
  login: string
  support: string
}

const useNavbarScroll = () => {
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', handleScroll)

    // call to set the initial state
    handleScroll()

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return {
    isScrolled,
  }
}

export type NavbarDisplay = 'transparent' | 'solid'

export const NavbarLayout = ({
  menuItems,
  headerDisplay = 'solid',
  signupLink,
  loginLink,
  supportLink,
}: HeaderProps) => {
  const isLoSignupCopyActive = useFeatureFlag('lo-signup-copy', false)
  const { isScrolled } = useNavbarScroll()

  const display = headerDisplay === 'solid' || isScrolled ? 'solid' : 'transparent'

  const signupFallback = isLoSignupCopyActive
    ? 'https://app.nav.com/registration?from=signup'
    : 'https://app.nav.com/registration'
  const loginFallback = isLoSignupCopyActive ? 'https://app.nav.com/login?from=open_app' : 'https://app.nav.com/login'

  const ctaLinks: NavbarCtaLinks = {
    signup: resolveHref(signupLink) || signupFallback,
    login: resolveHref(loginLink) || loginFallback,
    support: resolveHref(supportLink) || 'https://help.nav.com',
  }

  return (
    <div
      className={`${display === 'solid' ? 'bg-white' : 'bg-transparent'} transition-all) fixed z-[2000] w-full print:hidden`}
    >
      <Gutter>
        <div className="block md:hidden">
          <VerticalMenu menuItems={menuItems} display={display} ctaLinks={ctaLinks} />
        </div>

        <div className="hidden md:block">
          <HorizontalMenu menuItems={menuItems} display={display} ctaLinks={ctaLinks} />
        </div>
      </Gutter>
    </div>
  )
}
